import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { AdminService } from './admin.service';
import { LogService } from '../../shared/services/log.service';

const TOKEN_PARAM = 'switch_token';
const EMAIL_PARAM = 'switch_email';

function handoffParams(): URLSearchParams {
  return new URLSearchParams(window.location.hash.replace(/^#/, ''));
}

/** Whether this page load carries a switch-user handoff, checked without constructing anything. */
export function hasSwitchUserHandoff(): boolean {
  const params: URLSearchParams = handoffParams();
  return !!params.get(TOKEN_PARAM) && !!params.get(EMAIL_PARAM);
}

/**
 * Consumes an admin "switch user" handed over by the platform admin console.
 *
 * The console runs on another host and therefore cannot write this app's localStorage. It mints
 * the impersonation token against the gateway and passes it in the URL *fragment*, which the
 * browser never sends to a server.
 *
 * This runs as the FIRST app initializer, deliberately ahead of the IAM SDK's session restore:
 * that restore would otherwise mint the admin's own app token and drop them back into their own
 * session. With the impersonation markers already written, it takes `MtmAuthGlueService`'s
 * impersonation branch instead. Running before the router also means no landing route is needed —
 * the console can hand over on any URL, and the normal post-bootstrap navigation takes it from
 * there.
 */
@Injectable({ providedIn: 'root' })
export class SwitchUserHandoffService {
  private readonly adminService: AdminService = inject(AdminService);
  private readonly logService: LogService = inject(LogService);

  public consume$(): Observable<unknown> {
    const params: URLSearchParams = handoffParams();
    const token: string | null = params.get(TOKEN_PARAM);
    const email: string | null = params.get(EMAIL_PARAM);
    if (!token || !email) {
      return of(void 0);
    }
    // Drop the token from the address bar and from this history entry before anything can read it
    // back — a bookmark, the next page's referrer, or a copied URL.
    history.replaceState(null, '', window.location.pathname + window.location.search);

    return this.adminService.switchUserFromConsole(token, email).pipe(
      catchError((error: unknown) => {
        // Leave no half-built session behind; the app then boots to the login page as usual.
        this.adminService.clearOriginalStorage();
        this.logService.logError(error as Error);
        return of(void 0);
      })
    );
  }
}
